import type * as tp from '@base/utils/types'
import { useAppIndexStore } from '@base/stores/indexStore'
import { ut_returnNewRoute, ut_sendToLog } from '@base/utils/utils'
import { usePathStore } from '@base/stores/pathStore'

// Consecutive client redirects without a navigation that settles in between. Client only: on the
// server this module is shared by all requests, and it is only touched in the client branch below.
let redirectCount: number = 0
const MAX_REDIRECTS: number = 5

export default defineNuxtRouteMiddleware(async (to, from) => {
  const indexStore = useAppIndexStore()
  const event = useRequestEvent()

  // If it is redirect vom MSAL Login leave immediately
  if (to.hash.search('#state=') >= 0 || to.hash.search('#code=') >= 0) {
    return
  }

  // Store scroll position
  if (import.meta.client && window && from) {
    const scrollPos = {
      top: window.scrollY,
      left: window.scrollX,
    }
    indexStore.setScrollPos(from.path, scrollPos)
  }

  // Params for store init
  const paramRouter: tp.IParamRouter = {
    path: to.path,
    query: to.query,
    hash: to.hash,
    // Read here, where a Nuxt instance exists on both sides, and passed on as data — see IParamRouter.
    host: useRequestURL().host,
  }
  // Modify basePath encodeURI to fit paramRouter.path
  if (paramRouter.query.basepath) {
    paramRouter.query.basepath = encodeURI(paramRouter.query.basepath)
  }

  // A print render carries its parameters in the POST body instead of the query (`?post=1`, built
  // in pdf.ts for custom data, where the volume is open ended). There the body IS the query.
  if (paramRouter.query.post === '1') {
    const body = await awaitPostBody(event)
    if (body) {
      paramRouter.query = body
    }
  }

  // Log to server
  if (useRuntimeConfig().public.sendToLog) {
    await ut_sendToLog('client:' + import.meta.client + '|' + JSON.stringify(paramRouter))
  }

  // Initialize store if data already loaded by ssr or path is invalid
  let loaded: boolean = true
  if (!(import.meta.client && (indexStore.state.paramData.dataLoaded && paramRouter.query.rawurl !== '1')) && !indexStore.state.paramData.redirectpath) {
    loaded = await indexStore.callInitAction(paramRouter)
  }
  else {
    indexStore.setParamDataMutation({ dataLoaded: false })
  }

  // ⚑ SSR without data: answer 503, never an empty page with 200. When the pfinder server turns the
  // BATCH away (overload) or fails, the page used to render anyway — 200, nearly empty, "Error fetching
  // data" as its heading — and a crawler indexed exactly that (reproduced 12.10.2026 with a proxy
  // answering /batch with 503). A 503 with Retry-After makes it come back later; the CMS embedding
  // example passes the status on. In the browser nothing changes: the failed navigation stays
  // retryable, see callInitAction.
  if (import.meta.server && !loaded) {
    if (useRuntimeConfig().public.sendToLog) {
      await ut_sendToLog('Return 503:' + '|' + JSON.stringify(paramRouter))
    }
    event?.node.res.setHeader('Retry-After', '60')
    throw createError({
      status: 503,
      statusText: 'Service Unavailable',
    })
  }

  // Return empty, CMS must handle request only server call NOT ACTIVE not working
  if (import.meta.server && indexStore.state.paramData.redirectpath && paramRouter.query.embedded === '1') {
    if (useRuntimeConfig().public.sendToLog) {
      await ut_sendToLog('Return 404:' + '|' + JSON.stringify(paramRouter))
    }
    throw createError({
      status: 404,
      statusText: 'Page Not Found',
    })
  }

  // Redirect only possible on client
  if (import.meta.client) {
    // Redirect or 404 because of redirectpath
    if (indexStore.state.paramData.redirectpath) {
      // Not embedded redirect
      if (paramRouter.query.embedded !== '1') {
        let redirectpath: string = indexStore.state.paramData.redirectpath
        // If only trailing slash is wrong we need to add # to redirectpath otherwise same lo_route
        if (redirectpath + '/' === indexStore.state.paramData.slugPath) {
          redirectpath += '#'
        }
        indexStore.setParamDataMutation({ redirectpath: '' }) // Clear redirect path
        // Set stage if exists
        const query = indexStore.state.paramData.queryUrlServer.stage ? { stage: indexStore.state.paramData.queryUrlServer.stage } : {}
        const target = { path: indexStore.getCountryLangPath + '/' + redirectpath, query }
        // ⚑ Stop instead of redirecting forever. A redirect to the URL being navigated to can never settle:
        // state.paramData does not advance on a redirect, so the next round computes the same target again.
        // Seen LIVE since 21.07.2026: an embedded app (the route query carries no `embedded` after the first
        // load) navigated to an old URL without the language segment, redirected to `…/de_h/de/ ` and
        // repeated that ~20/s for hours. Vue Router only detects this in dev builds. The counter catches
        // chains that alternate between targets. Aborting keeps the visitor on the last valid page.
        if (useRouter().resolve(target).fullPath === to.fullPath || ++redirectCount > MAX_REDIRECTS) {
          redirectCount = 0
          if (useRuntimeConfig().public.sendToLog) {
            await ut_sendToLog('Redirect loop stopped:' + '|' + to.fullPath + '|' + target.path)
          }
          return abortNavigation()
        }
        // Replace URL
        if (useRuntimeConfig().public.sendToLog) {
          await ut_sendToLog('Redirect not embedded:' + '|' + target.path)
        }
        return navigateTo(target, { replace: true })
      }
    }
    // Check for special query param to replace route: Raw URL, embedded, force loading => replace URL
    else {
      redirectCount = 0
      const query = paramRouter.query
      if (query.rawurl === '1' || query.embedded === '1' || query.forceloading === '1') {
        const newRoute = ut_returnNewRoute('', {}, '')
        // Save path data also under new real path
        if (query.rawurl === '1') {
          usePathStore().copyPathData(indexStore.state.paramData, newRoute.path)
        }
        indexStore.setParamDataMutation({ realpath: newRoute.path, dataLoaded: true })
        if (useRuntimeConfig().public.sendToLog) {
          await ut_sendToLog('Replace URL:' + '|' + newRoute)
        }
        return navigateTo(newRoute, { replace: query.rawurl !== '1' }) // ToDo check replace false/true embedded and internal
      }
    }
  }
})

/**
 * The POST body of a print render, as soon as it is there.
 *
 * `server/middleware/handlePost.ts` puts it into the event context, and nitro awaits that handler
 * before the render — so in practice it is present on the very first look. This used to wait a
 * fixed second before looking at all, which cost every rendered page that second, whether or not
 * the body was there. Measured 07.09.2026 by POSTing a custom-data page the way pdf.ts does:
 * 1449-1555 ms before, 77-426 ms after, byte-identical markup. The second was pure waiting.
 *
 * The second is kept as a BUDGET rather than dropped: it costs nothing in the normal case, and if
 * the body should ever arrive late after all, the behaviour is the one this always had.
 *
 * On the client there is no event and never will be one, so it returns at once — no page pays for
 * a body that cannot exist during hydration.
 */
async function awaitPostBody(event: ReturnType<typeof useRequestEvent>, budgetMs: number = 1000, stepMs: number = 20): Promise<unknown> {
  if (!event) { return undefined }
  const deadline: number = Date.now() + budgetMs
  for (;;) {
    if (event.context?.body) { return event.context.body }
    if (Date.now() >= deadline) { return undefined }
    await new Promise(resolve => setTimeout(resolve, stepMs))
  }
}
